import {useMemo} from "react";
import {Table, Tag, Tooltip} from "antd";
import className from "classnames/bind";
import {useGetCompetitorReportQuery} from "~/reduxs/api/competitorApiSlice";
import style from "../style/Competitor.module.scss";

const cn = className.bind(style);

/** Icon trạng thái 1 tiêu chí của SeoAnalyzer (pass/warn/fail). */
const STATUS_TAG = {
	pass: <Tag color="green">Đạt</Tag>,
	warn: <Tag color="orange">Cần cải thiện</Tag>,
	fail: <Tag color="red">Chưa đạt</Tag>,
};

/**
 * Drill-down so TỪNG tiêu chí giữa 2 snapshot (2 lần gọi reports/{id} — detail cùng shape
 * api/seo/{id}: groups → items {key,label,status,score,max,message}). Hàng nào 2 bên lệch
 * trạng thái được đưa lên đầu để thấy ngay khác biệt.
 */
function CompareDetail({projectId, leftReportId, rightReportId}) {

	const {data: left, isFetching: l1} = useGetCompetitorReportQuery({projectId, reportId: leftReportId});
	const {data: right, isFetching: l2} = useGetCompetitorReportQuery({projectId, reportId: rightReportId});

	const rows = useMemo(() => {
		if (!left || !right) return [];

		const byKey = new Map();

		(left.items || []).forEach((it) => byKey.set(it.key, {key: it.key, group: it.group, label: it.label, left: it}));
		(right.items || []).forEach((it) => {
			const row = byKey.get(it.key) || {key: it.key, group: it.group, label: it.label};
			row.right = it;
			byKey.set(it.key, row);
		});

		const list = [...byKey.values()];

		// Tiêu chí lệch trạng thái lên đầu — đó là chỗ cần nhìn.
		const rank = (r) => (r.left?.status !== r.right?.status ? 0 : 1);
		list.sort((a, b) => rank(a) - rank(b));

		return list;
	}, [left, right]);

	const groupLabel = useMemo(() => {
		const map = {};
		[...(left?.groups || []), ...(right?.groups || [])].forEach((g) => { map[g.key] = g.label; });
		return map;
	}, [left, right]);

	const side = (it) => it ? (
		<Tooltip title={it.message || ''}>
			<span style={{display: 'inline-flex', alignItems: 'center', gap: 6}}>
				{STATUS_TAG[it.status] || null}
				<span className={cn('hint')}>{it.score}/{it.max}</span>
			</span>
		</Tooltip>
	) : <span className={cn('hint')}>—</span>;

	const columns = [
		{title: 'Tiêu chí', dataIndex: 'label', key: 'label', ellipsis: true},
		// Cột phụ — ẩn dưới 768px cho gọn mobile.
		{title: 'Nhóm', dataIndex: 'group', key: 'group', width: 120, responsive: ['md'],
			render: (v) => <span className={cn('hint')}>{groupLabel[v] || v}</span>},
		{title: left?.domain || 'Bên trái', key: 'left', width: 190, align: 'center', render: (_, r) => side(r.left)},
		{title: right?.domain || 'Bên phải', key: 'right', width: 190, align: 'center', render: (_, r) => side(r.right)},
		{
			title: '', key: 'diff', width: 60, align: 'center',
			render: (_, r) => r.left?.status !== r.right?.status
				? <Tag color="purple">lệch</Tag>
				: null,
		},
	];

	return (
		<Table
			rowKey="key"
			size="small"
			loading={l1 || l2}
			columns={columns}
			dataSource={rows}
			scroll={{x: 'max-content'}}
			pagination={{pageSize: 20, hideOnSinglePage: true, showSizeChanger: false}}
		/>
	);
}

export default CompareDetail;
